import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { first, takeUntil } from 'rxjs/operators';

import {
  EnergyHistoricalDataFeaturesGroup,
  EnergyHistoricalDataStore,
  hemsPropertiesUnits,
  SelectableFeature,
  SelectableFeaturesGroup,
} from '@store';
import { Subject } from 'rxjs';

import { powerChartFeatures, thermalChartFeatures } from '@vi-iot/client';

import { isFeatureOnForbiddenListForGivenFilter } from '../../../../@store/energy-historical-data/energy-historical-data.helpers';

import { ActivatedRoute } from '@angular/router';
import { FilterType } from '../energy-filters-header/energy-filters.data';

@Component({
  selector: 'app-chart-parameters-popup',
  templateUrl: './chart-parameters-popup.component.html',
  styleUrls: ['./chart-parameters-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartParametersPopupComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  readonly MAX_DISPLAYED_FEATURES = 6;

  @Input() isThermalFlow = false;
  @Output() popupClosed = new EventEmitter();

  chartFeatures: EnergyHistoricalDataFeaturesGroup[];
  selectableFeaturesGroups: SelectableFeaturesGroup[];
  propertiesUnits = hemsPropertiesUnits;
  parametersSelected = 1;
  unitsSelected = 1;
  installationId: string;

  activeFilterType: FilterType;
  listenToClickEvents = false;

  @HostListener('document:click', ['$event.target'])
  public onPageClick(targetElement): void {
    if (!this.listenToClickEvents) {
      return;
    }

    const clickedOutside = !this.elementRef.nativeElement.contains(targetElement);
    if (clickedOutside) {
      this.onClose();
    }
  }

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private energyHistoricalDataStore: EnergyHistoricalDataStore,
    private translateService: TranslateService,
    private elementRef: ElementRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((params) => {
      this.installationId = params.get('id');
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.listenToClickEvents = true;
    });
  }

  ngOnChanges(): void {
    this.energyHistoricalDataStore.filterType$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((filterType) => (this.activeFilterType = filterType));

    this.chartFeatures = this.isThermalFlow ? thermalChartFeatures : powerChartFeatures;
    this.createSelectableFeatures();
    this.initSelectedFeatures();
    this.initSummaryParametersCounters();
  }

  initSelectedFeatures(): void {
    this.energyHistoricalDataStore.selectedFeatures$.pipe(first()).subscribe((selectedFeatures) => {
      selectedFeatures.forEach((entry) => {
        this.selectableFeaturesGroups.forEach((featureGroup) => {
          const foundFeature = featureGroup.features.find((feature) => feature.name === entry);

          if (foundFeature) {
            foundFeature.checked = true;
          }
        });
      });
    });
  }

  toggleFeatureSelection(feature: SelectableFeature): void {
    feature.checked = !feature.checked;
  }

  isCheckboxDisabled(feature: SelectableFeature): boolean {
    let selectedFeatures = this.getSelectedFeatures();

    const maximumUniqueUnitsLimitReached = this.isMaximumUniqueUnitsLimitReached(
      feature,
      selectedFeatures
    );
    const maximumFeaturesSelected = this.isMaximumFeaturesLimitReached(selectedFeatures);
    const checkboxNotAlreadyChecked = !feature.checked;
    const isForbiddenFeature = isFeatureOnForbiddenListForGivenFilter(
      feature.name,
      this.activeFilterType
    );

    return (
      (maximumFeaturesSelected && checkboxNotAlreadyChecked) ||
      maximumUniqueUnitsLimitReached ||
      isForbiddenFeature
    );
  }

  onApplyParameters(): void {
    let selectedFeatures = [];

    this.selectableFeaturesGroups.forEach((featuresGroup) => {
      const checkedFeatures = featuresGroup.features
        .filter((entry) => entry.checked)
        .map((entry) => entry.name);
      selectedFeatures = selectedFeatures.concat(checkedFeatures);
    });

    this.energyHistoricalDataStore.updateSelectedFeatures(selectedFeatures);
    this.energyHistoricalDataStore.saveSelectedFeaturesToStorage(
      this.installationId,
      this.isThermalFlow,
      selectedFeatures
    );

    this.popupClosed.emit();
  }

  onClose(): void {
    this.popupClosed.emit();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private getSelectedFeatures(): SelectableFeature[] {
    let selectedFeatures: SelectableFeature[] = [];

    this.selectableFeaturesGroups.forEach((featuresGroup) => {
      const checkedFeatures = featuresGroup.features.filter((entry) => entry.checked);
      selectedFeatures = selectedFeatures.concat(checkedFeatures);
    });

    return selectedFeatures;
  }

  private initSummaryParametersCounters(): void {
    const selectedFeatures = this.getSelectedFeatures();
    this.parametersSelected = selectedFeatures.length;
    this.unitsSelected = [...new Set(selectedFeatures.map((feature) => feature.unit))].length;
  }

  private isMaximumFeaturesLimitReached(selectedFeatures: SelectableFeature[]): boolean {
    this.parametersSelected = selectedFeatures.length;

    return selectedFeatures.length === this.MAX_DISPLAYED_FEATURES;
  }

  private isMaximumUniqueUnitsLimitReached(
    feature: SelectableFeature,
    selectedFeatures: SelectableFeature[]
  ): boolean {
    const featureUnit = this.propertiesUnits.find((entry) => entry.property === feature.name)?.unit;
    const uniqueUnitsSelected = [];

    selectedFeatures.forEach((singleFeature) => {
      const entryUnit = this.propertiesUnits.find((entry) => entry.property === singleFeature.name)
        ?.unit;

      if (!uniqueUnitsSelected.includes(entryUnit)) {
        uniqueUnitsSelected.push(entryUnit);
      }
    });

    const isFeatureUnitWithinAlreadySelectedUnits = uniqueUnitsSelected.includes(featureUnit);
    const maximumUniqueUnitsLimitReached = uniqueUnitsSelected.length > 1;
    this.unitsSelected = uniqueUnitsSelected.length;

    return !isFeatureUnitWithinAlreadySelectedUnits && maximumUniqueUnitsLimitReached;
  }

  private createSelectableFeatures(): void {
    let featuresGroups: SelectableFeaturesGroup[] = [];

    this.chartFeatures.forEach((featuresGroup) => {
      const mappedGroup: SelectableFeaturesGroup = {
        groupKey: featuresGroup.groupKey,
        features: [],
      };

      mappedGroup.features = featuresGroup.features.map((feature) => ({
        name: feature.feature,
        displayName: this.translateService.instant(
          `+HEMS_FEATURES.${feature.feature.toUpperCase()}`
        ),
        checked: false,
        unit: feature.unit,
      }));

      featuresGroups.push(mappedGroup);
    });

    this.selectableFeaturesGroups = featuresGroups;
  }
}
